import React, { forwardRef } from 'react';
import Select, {
    type Props as SelectProps,
    type MultiValue,
    type SingleValue,
    type ActionMeta,
    type StylesConfig,
    type GroupBase
} from 'react-select';

export interface Option {
    value: string | number;
    label: string;
    isDisabled?: boolean;
}

export interface OptionGroup {
    label: string;
    options: Option[];
}

export type MultiSelectProps = Omit<SelectProps<Option, boolean, GroupBase<Option>>, 'onChange'> & {
    label?: string;
    error?: string;
    helpText?: string;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
    onChange?: (newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => void;
    // For compatibility with Choices.js getValue(true) pattern
    getValue?: (asArray?: boolean) => MultiValue<Option> | SingleValue<Option> | string[] | string | number;
    // For compatibility with Choices.js setChoiceByValue pattern
    setChoiceByValue?: (value: string | number | (string | number)[]) => void;
    // For compatibility with Choices.js disable pattern
    disable?: () => void;
};

const MultiSelect = forwardRef<any, MultiSelectProps>(
    (
        {
            label,
            error,
            helpText,
            fullWidth = true,
            size = 'md',
            className = '',
            isMulti = false,
            isClearable = true,
            closeMenuOnSelect = !isMulti,
            onChange,
            ...rest
        },
        ref
    ) => {
        // Custom styles to match Tailwind theme
        const customStyles: StylesConfig<Option, boolean, GroupBase<Option>> = {
            control: (provided, state) => ({
                ...provided,
                backgroundColor: '#333',
                borderColor: error ? '#f8564f' : state.isFocused ? '#0275d8' : '#444',
                boxShadow: error ? '0 0 0 2px rgba(248, 86, 79, 0.25)' : state.isFocused ? '0 0 0 2px rgba(2, 117, 216, 0.25)' : 'none',
                '&:hover': {
                    borderColor: error ? '#f8564f' : '#0275d8'
                },
                minHeight: size === 'sm' ? '30px' : size === 'lg' ? '48px' : '38px',
                fontSize: size === 'sm' ? '0.875rem' : size === 'lg' ? '1.125rem' : '1rem',
                color: '#fff'
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: size === 'sm' ? '0 8px' : size === 'lg' ? '6px 12px' : '2px 12px'
            }),
            input: (provided) => ({
                ...provided,
                margin: '0',
                padding: '0',
                color: '#fff'
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                height: size === 'sm' ? '30px' : size === 'lg' ? '48px' : '38px'
            }),
            dropdownIndicator: (provided) => ({
                ...provided,
                padding: size === 'sm' ? '4px' : size === 'lg' ? '8px' : '6px',
                color: '#7c8083',
                '&:hover': {
                    color: '#fff'
                }
            }),
            clearIndicator: (provided) => ({
                ...provided,
                padding: size === 'sm' ? '4px' : size === 'lg' ? '8px' : '6px',
                color: '#7c8083',
                '&:hover': {
                    color: '#f8564f'
                }
            }),
            menu: (provided) => ({
                ...provided,
                backgroundColor: '#343a40',
                border: '1px solid #444',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? '#0275d8' : state.isFocused ? 'rgba(2, 117, 216, 0.2)' : 'transparent',
                color: state.isSelected ? '#fff' : '#f8f9fa',
                '&:hover': {
                    backgroundColor: state.isSelected ? '#0275d8' : 'rgba(2, 117, 216, 0.2)'
                }
            }),
            multiValue: (provided) => ({
                ...provided,
                backgroundColor: '#444',
                borderRadius: '0.25rem'
            }),
            multiValueLabel: (provided) => ({
                ...provided,
                fontSize: size === 'sm' ? '0.75rem' : size === 'lg' ? '1rem' : '0.875rem',
                padding: size === 'sm' ? '2px 4px' : size === 'lg' ? '4px 8px' : '3px 6px',
                color: '#f8f9fa'
            }),
            multiValueRemove: (provided) => ({
                ...provided,
                paddingLeft: '4px',
                paddingRight: '4px',
                '&:hover': {
                    backgroundColor: '#f8564f',
                    color: 'white'
                }
            }),
            placeholder: (provided) => ({
                ...provided,
                color: '#7c8083'
            }),
            singleValue: (provided) => ({
                ...provided,
                color: '#f8f9fa'
            })
        };

        // Container classes
        const containerClasses = `mb-4 ${fullWidth ? 'w-full' : ''} ${className}`;

        // Add compatibility methods to the ref
        const selectRef = React.useRef<any>(null);

        // Combine refs
        React.useImperativeHandle(ref, () => ({
            ...selectRef.current,
            // Compatibility with Choices.js getValue method
            getValue: (asArray = false) => {
                if (!selectRef.current) return isMulti ? [] : null;

                const value = selectRef.current.getValue();

                if (isMulti) {
                    if (asArray) {
                        return value.map((v: Option) => v.value);
                    }

                    return value;
                } else {
                    if (value.length === 0) return null;
                    return asArray ? [value[0].value] : value[0].value;
                }
            },
            // Compatibility with Choices.js setChoiceByValue method
            setChoiceByValue: (value: string | number | (string | number)[]) => {
                if (!selectRef.current) return;

                const options = rest.options || [];
                let flatOptions: Option[] = [];

                // Flatten option groups if needed
                options.forEach((opt) => {
                    if (opt && 'options' in opt && Array.isArray(opt.options)) {
                        flatOptions = [...flatOptions, ...opt.options];
                    } else if ('value' in opt && 'label' in opt) {
                        flatOptions.push(opt);
                    }
                });

                if (isMulti && Array.isArray(value)) {
                    const selectedOptions = flatOptions.filter((opt) => value.includes(opt.value));
                    selectRef.current.setValue(selectedOptions);
                } else if (!isMulti && !Array.isArray(value)) {
                    const selectedOption = flatOptions.find((opt) => opt.value === value);

                    if (selectedOption) {
                        selectRef.current.setValue(selectedOption);
                    }
                }
            },
            // Compatibility with Choices.js disable method
            disable: () => {
                if (!selectRef.current) return;
                selectRef.current.props.isDisabled = true;
                selectRef.current.forceUpdate();
            }
        }));

        return (
            <div className={containerClasses}>
                {label && <label className="block text-sm font-medium text-zinc-200 mb-1">{label}</label>}
                <Select
                    className={`react-select-container ${error ? 'border-red-500' : ''}`}
                    classNamePrefix="react-select"
                    closeMenuOnSelect={closeMenuOnSelect}
                    isClearable={isClearable}
                    isMulti={isMulti}
                    ref={selectRef}
                    styles={customStyles}
                    onChange={onChange}
                    {...rest}
                />
                {helpText && <div className="mt-1 text-sm text-zinc-400">{helpText}</div>}
                {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
            </div>
        );
    }
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
